import React, { useMemo } from "react";
import { useLang } from "../context/LangContext";

// Returns YYYY-MM-DD string for a given Date
const fmt = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const MONTH_LABELS_MN = [
  "1-р сар","2-р сар","3-р сар","4-р сар","5-р сар","6-р сар",
  "7-р сар","8-р сар","9-р сар","10-р сар","11-р сар","12-р сар",
];
const MONTH_LABELS_EN = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DOW_MN = ["Да", "Мя", "Лх", "Пү", "Ба", "Бя", "Ня"];
const DOW_EN = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

function MonthView({ year, month, bookedSet, selectedRange, onPick, interactive }) {
  const { lang } = useLang();
  const monthLabels = lang === "mn" ? MONTH_LABELS_MN : MONTH_LABELS_EN;
  const dowLabels = lang === "mn" ? DOW_MN : DOW_EN;

  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // Monday-based offset
  const startOffset = (first.getDay() + 6) % 7;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const { start, end } = selectedRange || {};

  return (
    <div className="card p-5">
      <div className="flex items-baseline justify-between mb-3">
        <h4 className="font-serif text-2xl">{monthLabels[month]}</h4>
        <span className="text-xs opacity-60">{year}</span>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dowLabels.map((d) => (
          <div key={d} className="text-center text-[10px] tracking-widest uppercase opacity-60">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const dateObj = new Date(year, month, d);
          const iso = fmt(dateObj);
          const isPast = dateObj < today;
          const isBooked = bookedSet.has(iso);
          const isStart = start && iso === start;
          const isEnd = end && iso === end;
          const isInRange =
            start && end && iso > start && iso < end;
          const classes = ["cal-day"];
          if (isPast) classes.push("cal-day-past");
          else if (isBooked) classes.push("cal-day-booked");
          else classes.push("cal-day-available");
          if (isStart || isEnd) classes.push("cal-day-selected");
          else if (isInRange) classes.push("cal-day-in-range");

          const clickable = interactive && !isPast && !isBooked;
          return (
            <button
              key={i}
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onPick && onPick(iso)}
              className={classes.join(" ")}
              data-testid={`cal-day-${iso}`}
              data-booked={isBooked ? "true" : "false"}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ThreeMonthCalendar({
  bookedDates = [],
  selectedRange,
  onPick,
  interactive = true,
  monthsAhead = 3,
}) {
  const { t } = useLang();
  const bookedSet = useMemo(() => new Set(bookedDates), [bookedDates]);

  const months = useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < monthsAhead; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      out.push({ year: d.getFullYear(), month: d.getMonth() });
    }
    return out;
  }, [monthsAhead]);

  return (
    <div className="space-y-5" data-testid="three-month-calendar">
      <div className="flex flex-wrap items-center gap-4 text-xs opacity-80">
        <span className="inline-flex items-center gap-2">
          <span className="w-3 h-3 rounded-full" style={{ background: "var(--secondary)" }} />
          {t("available")}
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="w-3 h-3 rounded-full line-through" style={{ background: "var(--muted)" }} />
          {t("booked")}
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="w-3 h-3 rounded-full" style={{ background: "var(--primary)" }} />
          {t("selected")}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-4">
        {months.map((m, i) => (
          <MonthView
            key={i}
            year={m.year}
            month={m.month}
            bookedSet={bookedSet}
            selectedRange={selectedRange}
            onPick={onPick}
            interactive={interactive}
          />
        ))}
      </div>
    </div>
  );
}
